import { useQuery } from '@tanstack/react-query';
import { api } from './client';
import { useScopedSelect } from '@/contexts/ClientScopeContext';

/**
 * RMM module (Datto RMM) — types and hooks. Shapes mirror the DTOs built in
 * `server/rmm/normalise.ts`; the browser never sees a raw Datto object.
 */

export interface RmmHardware {
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
}

export interface RmmDevice {
  uid: string;
  id: number;
  hostname: string;
  description: string | null;
  siteUid: string;
  siteName: string;
  /** Autotask company linked to the device's site. `0` is a real id (Synfo's internal company). */
  autotaskCompanyId: number | null;
  autotaskCompanyName: string | null;
  category: string | null;
  type: string | null;
  deviceClass: string;
  operatingSystem: string | null;
  a64Bit: boolean;
  intIpAddress: string | null;
  extIpAddress: string | null;
  domain: string | null;
  lastLoggedInUser: string | null;
  agentVersion: string | null;
  online: boolean;
  suspended: boolean;
  rebootRequired: boolean;
  lastSeen: string | null;
  lastReboot: string | null;
  lastAuditDate: string | null;
  creationDate: string | null;
  warrantyDate: string | null;
  patchStatus: string | null;
  patchesApprovedPending: number | null;
  patchesNotApproved: number | null;
  patchesInstalled: number | null;
  antivirusProduct: string | null;
  antivirusStatus: string | null;
  softwareStatus: string | null;
  portalUrl: string;
  webRemoteUrl: string | null;
  openAlerts: number;
  topAlertPriority: string | null;
  hardware: RmmHardware | null;
}

export interface RmmSite {
  uid: string;
  name: string;
  description: string | null;
  portalUrl: string;
  autotaskCompanyId: number | null;
  autotaskCompanyName: string | null;
  deviceCount: number;
}

export interface RmmDevicesResponse {
  items: RmmDevice[];
  fetchedAt: string;
  sites: RmmSite[];
  /** Background hardware-audit progress — hardware columns are blank until a device is audited. */
  hardware: { audited: number; total: number };
}

export interface RmmAlert {
  alertUid: string;
  priority: string;
  timestamp: string;
  kind: string;
  summary: string;
  ticketNumber: string | null;
  /** Autotask ticket id resolved from `ticketNumber`, when found. */
  ticketId: number | null;
  createsTicket: boolean;
  muted: boolean;
  diagnostics: string | null;
}

export interface RmmAudit {
  manufacturer: string | null;
  model: string | null;
  serialNumber: string | null;
  biosVersion: string | null;
  biosReleaseDate: string | null;
  baseBoard: string | null;
  processors: string[];
  cpuCores: number | null;
  memoryBytes: number | null;
  memoryModules: { module: string | null; size: number | null; speed: string | null }[];
  disks: { id: string | null; description: string | null; size: number | null; free: number | null }[];
  nics: { name: string | null; ipv4: string | null; ipv6: string | null; mac: string | null; type: string | null }[];
  videoAdapters: string[];
  dotNetVersion: string | null;
}

export interface RmmDeviceDetail {
  device: RmmDevice;
  udfs: { key: string; label: string; value: string }[];
  audit: RmmAudit | null;
  alerts: RmmAlert[];
}

// The server caches the account for ~3 min, so polling faster than that only re-reads its cache.
const DEVICES_REFRESH_MS = 3 * 60_000;

/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useRmmDevices(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<RmmDevicesResponse>(opts.scoped, (d, inScope) => ({
    ...d, items: d.items.filter((x) => inScope(x.autotaskCompanyId)), sites: d.sites.filter((s) => inScope(s.autotaskCompanyId)),
  }));
  return useQuery({
    select,
    queryKey: ['rmm', 'devices'],
    queryFn: () => api.get<RmmDevicesResponse>('/api/rmm/devices'),
    staleTime: DEVICES_REFRESH_MS,
    refetchInterval: DEVICES_REFRESH_MS,
  });
}

/** Is the device billed in Autotask — `server/rmm/billing.ts`. */
export type RmmBillingState = 'billed' | 'no-service' | 'expired' | 'no-contract' | 'no-ci' | 'internal';
export interface RmmDeviceBilling {
  state: RmmBillingState;
  ciId: number | null;
  companyId: number | null;
  companyName: string | null;
  contractId: number | null;
  contractName: string | null;
  contractEnd: string | null;
  serviceName: string | null;
}

/** Billing state per device uid. Served from a 15-min Autotask cache, so refresh slowly. */
export function useRmmBilling() {
  return useQuery({
    queryKey: ['rmm', 'billing'],
    queryFn: () => api.get<{ items: Record<string, RmmDeviceBilling>; fetchedAt: string }>('/api/rmm/billing'),
    staleTime: 5 * 60_000,
    refetchInterval: 15 * 60_000,
  });
}

export function useRmmDevice(uid: string | undefined) {
  return useQuery({
    enabled: !!uid,
    queryKey: ['rmm', 'device', uid],
    queryFn: () => api.get<RmmDeviceDetail>(`/api/rmm/devices/${uid}`),
    staleTime: 60_000,
  });
}

/** Installed software — fetched only when the Software tab is opened. */
export function useRmmDeviceSoftware(uid: string | undefined, enabled: boolean) {
  return useQuery({
    enabled: !!uid && enabled,
    queryKey: ['rmm', 'device', uid, 'software'],
    queryFn: () => api.get<{ items: { name: string; version: string | null }[] }>(`/api/rmm/devices/${uid}/software`),
    staleTime: 10 * 60_000,
  });
}

export interface RmmTicketContext {
  company: { id: number; name: string } | null;
  configurationItem: { id: number; name: string } | null;
  contact: { id: number; name: string; email: string | null; matchedBy: 'configuration item' | 'email' | 'username' } | null;
}

/** Pre-fill for the "Log ticket" modal — fetched when the modal opens. */
export function useRmmTicketContext(uid: string | undefined, enabled: boolean) {
  return useQuery({
    enabled: !!uid && enabled,
    queryKey: ['rmm', 'device', uid, 'ticket-context'],
    queryFn: () => api.get<RmmTicketContext>(`/api/rmm/devices/${uid}/ticket-context`),
    staleTime: 5 * 60_000,
  });
}

/** An open alert in the account-wide inbox, joined to its device, Autotask company and ticket. */
export interface RmmInboxAlert extends RmmAlert {
  deviceUid: string | null;
  deviceName: string | null;
  siteUid: string | null;
  siteName: string | null;
  autotaskCompanyId: number | null;
  autotaskCompanyName: string | null;
  deviceOnline: boolean | null;
  deviceCategory: string | null;
}

// Matches the server's alerts cache TTL.
const ALERTS_REFRESH_MS = 2 * 60_000;

/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useRmmAlerts(opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<{ items: RmmInboxAlert[]; fetchedAt: string }>(opts.scoped, (d, inScope) => ({ ...d, items: d.items.filter((x) => inScope(x.autotaskCompanyId)) }));
  return useQuery({
    select,
    queryKey: ['rmm', 'alerts'],
    queryFn: () => api.get<{ items: RmmInboxAlert[]; fetchedAt: string }>('/api/rmm/alerts'),
    staleTime: ALERTS_REFRESH_MS,
    refetchInterval: ALERTS_REFRESH_MS,
  });
}

// -------- Dashboards (payloads mirror server/rmm/dashboards.ts) --------

export type RmmTone = 'success' | 'warning' | 'serious' | 'destructive' | 'info' | 'neutral' | 'subtle';

export interface RmmBucket {
  id: string | number;
  label: string;
  value: number;
  tone?: RmmTone;
  /** Devices-page status filter a stale-age bucket drills into. */
  view?: string;
}

export interface RmmTrendPoint {
  day: string;
  devices: number;
  onlinePct: number | null;
  compliancePct: number | null;
  openAlerts: number;
  patchErrors: number;
  stale30: number;
}

export interface RmmDashboardScope {
  companyId: number | null;
  clients: { id: number; name: string; devices: number }[];
}

export interface RmmOverviewDashboard {
  scope: RmmDashboardScope;
  kpis: {
    devices: number; online: number; stale30: number; withAlerts: number; openAlerts: number;
    reboot: number; patchManaged: number; patchCompliant: number; patchErrors: number; avIssues: number;
  };
  alertsByPriority: RmmBucket[];
  alertsByType: RmmBucket[];
  topClientsByAlerts: RmmBucket[];
  devicesByType: RmmBucket[];
  devicesByOs: RmmBucket[];
  devicesByManufacturer: RmmBucket[];
  manufacturerUnaudited: number;
  staleBuckets: RmmBucket[];
  topClientsByStale: RmmBucket[];
  agentVersions: RmmBucket[];
  win11Readiness: RmmBucket[];
  trend: RmmTrendPoint[];
}

export interface RmmPatchRow {
  name: string;
  companyId: number | null;
  siteUid: string | null;
  devices: number;
  managed: number;
  compliant: number;
  compliancePct: number | null;
  pending: number;
  errors: number;
  reboot: number;
  noPolicy: number;
}

export interface RmmPatchingDashboard {
  scope: RmmDashboardScope;
  kpis: { compliancePct: number | null; managed: number; compliant: number; pending: number; errors: number; reboot: number; noPolicy: number; pendingPatches: number };
  byStatus: RmmBucket[];
  rows: RmmPatchRow[];
  rowKind: 'client' | 'site';
  trend: RmmTrendPoint[];
}

const DASH_STALE_MS = 2 * 60_000;
const scopeQs = (companyId: number | null) => (companyId != null ? `?companyId=${companyId}` : '');

export function useRmmOverviewDashboard(companyId: number | null) {
  return useQuery({
    queryKey: ['rmm', 'dashboard', 'overview', companyId],
    queryFn: () => api.get<RmmOverviewDashboard>(`/api/rmm/dashboards/overview${scopeQs(companyId)}`),
    staleTime: DASH_STALE_MS,
    refetchInterval: DASH_STALE_MS,
  });
}

export function useRmmPatchingDashboard(companyId: number | null) {
  return useQuery({
    queryKey: ['rmm', 'dashboard', 'patching', companyId],
    queryFn: () => api.get<RmmPatchingDashboard>(`/api/rmm/dashboards/patching${scopeQs(companyId)}`),
    staleTime: DASH_STALE_MS,
    refetchInterval: DASH_STALE_MS,
  });
}
